import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect, useRouteMatch } from 'react-router-dom';
import fetch from 'cross-fetch';
import { NS } from '../../../models/I18n';
import Button from '../../../shared/components/buttons/Button';
import { useSubmissionResultQuery, useUserInfoQuery, useProgramQuery } from '../../SubmissionAPI';
import { SuperVisasSideBar } from '../components/CrsSection';
import * as apiClient from '../../apiClient';
import { ErrorMessage } from '../../../platform-frontend/partner-dashboard/components/forms/AddUserForm';
import { getItemFromLocalStorage } from '../../../shared/utils/storage-api';
import { useRouterParamQuery } from '../../../shared/utils/useRouterParamQuery';
import Link from '../../../shared/components/links/Link';

export function UserAccountCreation() {
  const query = useRouterParamQuery();
  const programId = query.get('programId');
  const { t } = useTranslation(NS.Eligibility);
  const ref = useRef(null);
  const match = useRouteMatch<{ submissionId: string }>('/eligibility/:submissionId/sign_up');
  const [error, setError] = useState<Record<string, Array<string>> | null>(null);
  const emailErrors = error?.email || [];
  const passwordErrors = error?.password || [];
  const [isLoading, setIsLoading] = useState(false);
  const submissionId = match?.params.submissionId || '';
  const selectedLng = getItemFromLocalStorage('i18nextLng') || 'en';
  const { data: submissionResult } = useSubmissionResultQuery(submissionId);
  const { data: program } = useProgramQuery({ programId: parseInt(programId || '', 10) });
  const { data: userInfoResult } = useUserInfoQuery();
  if (!submissionResult || !userInfoResult) {
    return null;
  }
  const { lead, user, submission } = submissionResult;
  // !isLoading prevents redirecting to book_a_time when redirecting to the user portal
  if (!isLoading && (user || userInfoResult.user_info)) {
    return <Redirect to={`/eligibility/${submissionId}/book_a_time`} />;
  }
  if (!lead && !programId) {
    return <Redirect to={`/eligibility/${submissionId}/contact_info`} />;
  }

  // when programId is not provided, it means its simply a sign up
  // Otherwise, it means we need to sign up, create an application and
  // then redirect to the application page based on the application id
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (ref?.current) {
      setIsLoading(true);
      window.gtag?.('event', 'user_sign_up', {
        event_category: 'user_capture',
        event_label: 'eligibility',
      });
      try {
        const data = new FormData(ref.current);
        const userInfo = {
          first_name: data.get('first_name'),
          last_name: data.get('last_name'),
          email: data.get('email'),
          phone_number: lead?.phone_number || '',
          consent_marketing_emails: data.get('consent_marketing_emails') || false,
          password: data.get('password'),
        };
        const res = await apiClient.createUserFromDefaultLeadGenPortal({
          userInfo,
          submissionToken: submissionId,
        });
        const json = await res.json();
        // sends user contact info to Hubspot:
        const isBrowser = typeof window !== 'undefined';
        const pageUri = isBrowser ? window.location.href : null;
        const pageName = isBrowser ? document.title : null;
        const postUrl =
          'https://api.hsforms.com/submissions/v3/integration/submit/9408879/128fb74d-6e87-40c2-9d5c-bc695d07bdc6';
        const body = {
          submittedAt: Date.now(),
          fields: [
            {
              name: 'firstname',
              value: userInfo.first_name,
            },
            {
              name: 'lastname',
              value: userInfo.last_name,
            },
            {
              name: 'email',
              value: userInfo.email,
            },
            { name: 'intended_country', value: submission.country },
            { name: 'intended_program_type', value: submission.lead_intention },
            {
              name: 'program_they_started',
              value: programId ? `${program?.name || 'Program not found'} - ${programId}` : 'Program not selected',
            },
            {
              name: 'marketing_consent_from_website',
              value: userInfo.consent_marketing_emails === 'on' ? 'Yes' : 'No',
            },
          ],
          context: {
            pageUri,
            pageName,
          },
          legalConsentOptions: {
            consent: {
              // Include this object when GDPR options are enabled
              consentToProcess: true,
              text: 'I agree to the Privacy Policy',
            },
          },
        };
        fetch(postUrl, {
          method: 'post',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!programId) {
          window.location.href = `https://apply.${window.location.host}/user/newapplication?lng=${selectedLng}`;
        } else {
          const result = await apiClient.createApplicationFromDefaultLeadGenPortal({
            userId: json.user.id,
            programId,
            submissionId,
            subApplicant: undefined,
            isAssignedToSelf: undefined,
          });
          const application = await result.json();
          window.location.href = `https://apply.${window.location.host}/user/applications/${application.id}?lng${selectedLng}`;
        }
      } catch (err) {
        if (err instanceof apiClient.RequestError && err?.body) {
          setError(err.body);
          setIsLoading(false);
        }
      }
    }
  };

  // This renders when the submission has associated with a lead but not yet with a user.
  return (
    <div className="block lg:flex">
      <SuperVisasSideBar />
      <div className="w-full bg-white px-8 py-3 lg:min-h-screen lg:w-3/4 lg:pr-24">
        <div className="mb-2 text-base font-semibold text-gray-600 lg:text-2xl">
          {programId ? t('leadInfo.start-an-application') : t('leadInfo.create-an-account')}
        </div>
        <div className="mb-3 text-base text-gray-600">{t('leadInfo.save-progress')}</div>
        <form ref={ref} onSubmit={onSubmit}>
          <div className="block lg:flex lg:justify-between lg:gap-8">
            <div className="w-full">
              <label className="mb-1 inline-block font-medium text-gray-600">{t('leadInfo.first-name')}</label>
              <input
                id="id_first_name"
                name="first_name"
                defaultValue={lead?.first_name || ''}
                className="mb-6 block w-full rounded border-gray-400 text-gray-600 lg:mb-0"
                type="text"
                required
              />
            </div>
            <div className="w-full">
              <label className="mb-1 inline-block font-medium text-gray-600">{t('leadInfo.last-name')}</label>
              <input
                id="id_last_name"
                name="last_name"
                defaultValue={lead?.last_name || ''}
                className="block w-full rounded border-gray-400 text-gray-600"
                type="text"
                required
              />
            </div>
          </div>
          <div className="my-6">
            <label className="mb-1 inline-block font-medium text-gray-600">{t('leadInfo.email-address')}</label>
            <input
              id="id_email"
              name="email"
              defaultValue={lead?.email || ''}
              className="block w-full rounded border-gray-400 text-gray-600"
              type="email"
              required
            />
            <ErrorMessage errors={emailErrors} />
          </div>
          <div className="my-6">
            <label className="mb-1 inline-block font-medium text-gray-600">{t('leadInfo.password')}</label>
            <input
              id="id_password1"
              name="password"
              className="block w-full rounded border-gray-400 text-gray-600"
              type="password"
              autoComplete="new-password"
              required
            />
            <ErrorMessage errors={passwordErrors} />
          </div>
          <ul className="ml-4 list-disc font-medium text-gray-600">
            <li>{t('leadInfo.password-can-not-be-similar')}</li>
            <li>{t('leadInfo.password-must-contain')}</li>
            <li>{t('leadInfo.password-can-not-be-common')}</li>
            <li>{t('leadInfo.password-can-not-be-numeric')}</li>
          </ul>
          <div className="mb-12 font-medium text-gray-600">
            <label>
              <input
                id="id_consent_marketing_emails"
                name="consent_marketing_emails"
                className="mr-2 cursor-pointer"
                type="checkbox"
              />
              <span>{t('leadInfo.consent-marketing-emails')}</span>
            </label>
          </div>
          <input id="submission_token" name="submission_token" className="hidden" value={submissionId} readOnly />
          <div className="mb-12 font-medium text-gray-600">
            <Trans i18nKey="leadInfo.disclaimer" t={t}>
              By clicking Create Account, you agree to our{' '}
              <a
                href="https://supervisa-statics-production.s3.amazonaws.com/static/local/pdf/privacy-policy.pdf"
                target="_blank"
                className="text-brand-blue"
                rel="noreferrer"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://supervisa-statics-production.s3.amazonaws.com/static/local/pdf/terms-of-use-agreement.pdf"
                target="_blank"
                className="text-brand-blue"
                rel="noreferrer"
              >
                Term of Use
              </a>
              .
            </Trans>
          </div>
          <section className="mb-16 flex flex-col-reverse justify-center gap-3 lg:mb-0 lg:flex-row">
            <Link
              className="w-full text-center lg:w-auto"
              variant="secondary"
              to={`/eligibility/${submissionId}/${programId ? 'result' : 'book_a_time'}`}
            >
              {t('leadInfoView.back')}
            </Link>
            <Button className="w-full lg:w-auto" isLoading={isLoading || !program} type="submit">
              {t('leadInfo.create-account')}
            </Button>
          </section>
        </form>
      </div>
    </div>
  );
}
